import Dexie from 'dexie';
import type { DBCore, DBCoreTable, DBCoreTransaction, Table, Transaction } from 'dexie';
import {
  довестиОткрытие, имяОтказа, отказСоединения, попытатьсяПродвинутьПоколение, текущееПоколение,
} from './connectionGuard';
import { отпустить, следПереоткрытия } from '../sentry';
import type { FactId, Operation, Tile } from '../engine/facts';
import type { Hlc } from '../engine/hlc';
import type { ExerciseType } from '../engine/session';

export const SCHEMA_VERSION = 6;

/** Профиль ребёнка. Имени у него нет вовсе — только цвет и зверёк из
 *  закрытых списков: семилетка узнаёт себя по картинке быстрее, чем по
 *  строке, а в базе, в текстах диалогов и в сообщениях об ошибках нечему
 *  появиться. Свободного текста, вводимого пользователем, у профиля нет. */
export interface ProfileRow {
  id: string;
  color: string;
  animal: string;
  createdAt: number;
  /** Сервер об этом профиле уже знает. Пока признак не стоит, авторитетный
   *  список профилей не имеет права удалять его локально: профиль мог быть
   *  заведён офлайн, и вместе с ним удалился бы месяц занятий, которого
   *  больше нигде нет (фаза 3). */
  serverKnown: boolean;
  /** Надгробие. Профиль не исчезает из таблицы при удалении — исчезают его
   *  занятия, а строка остаётся с отметкой времени.
   *
   *  Довод тот же, что и на сервере, и он не про аккуратность. Устройство,
   *  бывшее офлайн в момент удаления, обязано когда-нибудь сообщить об этом
   *  серверу; если удалить строку насовсем, сообщать будет нечем — и
   *  авторитетный список вернёт профиль обратно вместе с занятиями, которые
   *  родитель нажатием «удалить навсегда» велел стереть. */
  deletedAt: number | null;
  /** Есть что отправить: облик заведён или изменён здесь и сервер об этом
   *  ещё не знает.
   *
   *  Без этого признака отправка слепо переписывает серверную строку своей
   *  копией — и смена цвета, сделанная на планшете, отменяется первой же
   *  синхронизацией телефона, у которого лежит устаревшая копия. Это не
   *  «последний выиграл», а мигание: устройства по очереди возвращают своё.
   *  Профили — единственная изменяемая сущность плана, и правило для них
   *  нужно именно здесь. */
  needsPush: boolean;
  schemaVersion: number;
}

export interface AttemptRow {
  id: string;
  profileId: string;
  sessionId: string;
  taskId: string;
  factIds: FactId[];
  leftFactId: FactId | null;
  exerciseType: ExerciseType;
  presented: unknown;
  showIndex: number;
  /** Факт о попытке, а не вывод из него: годность замера считается при
   *  чтении (раздел 6.2). */
  resumedAfterBackground: boolean;
  answerMs: number;
  isCorrect: boolean;
  given: unknown;
  expected: unknown;
  ordinal: number;
  seq: Hlc;
  deviceId: string;
  schemaVersion: number;
  presentedAt: number;
}

export interface RevealRow {
  id: string;
  profileId: string;
  factId: FactId;
  sessionId: string;
  /** Логические часы — только для упорядочивания (раздел 4.6). */
  seq: Hlc;
  /** Стенное время в миллисекундах — для измерения длительностей.
   *  Освоенность меряет по нему окно в 12 часов после раскрытия, а
   *  логические часы для длительностей непригодны. */
  revealedAt: number;
  deviceId: string;
  schemaVersion: number;
}

export interface SessionRow {
  id: string;
  profileId: string;
  startedAt: number;
  finishedAt: number;
  wallClockMs: number;
  tiles: Tile[];
  mode: Operation | 'mixed';
  taskCount: number;
  totalAttempts: number;
  correctAttempts: number;
  wrongAttempts: number;
  assistedTasks: number;
  accuracy: number | null;
  totalAnswerMs: number;
  avgAnswerMs: number | null;
  avgCorrectAnswerMs: number | null;
  sourceSessionId: string | null;
  /** Как у двух других журналов. Без него оба составных индекса слепы:
   *  IndexedDB не индексирует строку, у которой нет хотя бы одного
   *  компонента составного ключа, — схема применилась бы, `put` прошёл бы,
   *  `count()` показал бы строки, а диапазонный запрос отправки вернул бы
   *  пусто, и ни одна сессия никогда не уехала бы на сервер. Молча. */
  seq: Hlc;
  deviceId: string;
  schemaVersion: number;
}

/** Состояние пары «ребёнок × устройство». Ключ строки — `profileId`:
 *  курсор раздачи это свойство ребёнка на этом устройстве, а не устройства.
 *  Двое детей на одном планшете обязаны иметь независимые очереди раздачи,
 *  иначе один съедает выдачи другого и равномерное покрытие ломается для
 *  обоих (раздел 5.1 спеки).
 *
 *  `keyPath` остаётся `id`, а `profileId` кладётся в него значением: Dexie
 *  не умеет менять первичный ключ существующей таблицы —
 *  `UpgradeError: Not yet support for changing primary key`. В тестах этого
 *  не видно, там база всегда пустая. */
export interface StateRow {
  id: string;
  hlc: Hlc | null;
  dealerCursors: Record<string, { order: FactId[]; cursor: number }>;
  schemaVersion: number;
}

/** Состояние устройства, общее для всех профилей на нём. Переживает выход
 *  из учётной записи: идентификатор устройства и признак постоянного
 *  хранилища не про ребёнка, и их потеря только заставила бы заново
 *  просить постоянное хранилище. */
export interface DeviceRow {
  id: 'singleton';
  deviceId: string;
  storagePersisted: boolean | null;
  activeProfileId: string | null;
  schemaVersion: number;
  /** Чьи это учебные данные.
   *
   *  До появления этого поля ответа не было НИГДЕ: идентификатор родителя
   *  на устройство не писался вовсе, а сравнивать его было не с чем. Из-за
   *  этого сегодня возможно вот что. Родитель А вошёл на планшете; сессия
   *  умерла (`SIGNED_OUT` приходит и при неудачном обновлении токена);
   *  появился экран «войдите заново»; вошёл родитель Б — и получил экран, на
   *  котором вперемешку дети обеих семей, потому что местная база осталась
   *  прежней.
   *
   *  `null` значит «данные заведены до всякого входа». Такие усыновляет тот,
   *  кто войдёт первым, и это же поле делает возможной работу до входа.
   *  Чужой идентификатор — повод остановиться и спросить взрослого, а не
   *  повод молча стереть или молча смешать. */
  ownerId: string | null;
}

export type SyncTable = 'attempts' | 'reveals' | 'sessions';

/** Курсоры синхронизации. Ключуются парой «профиль × таблица», а не одной
 *  таблицей: `server_seq` сквозной по таблице и общий для всех детей, и
 *  устройство, докачавшее одного ребёнка до 900, никогда не запросило бы
 *  строки другого с меньшими номерами (раздел 4.1). */
export interface SyncStateRow {
  profileId: string;
  table: SyncTable;
  /** Докачано до этого `server_seq` включительно. */
  pulledUpTo: number;
  /** Отправлено до этой позиции в собственном пространстве
   *  `[deviceId+profileId+seq.ms+seq.counter+id]` включительно. `id` в
   *  составе обязателен: без него пара `(ms, counter)` неуникальна, и
   *  возобновление «строго выше курсора» теряет вторую строку с тем же
   *  ключом. */
  pushedUpTo: { ms: number; counter: number; id: string } | null;
}

/** Строка, пришедшая с сервера и не прошедшая проверку на входе.
 *
 *  Держится отдельно от журнала намеренно: класть непроверенную строку в
 *  журнал нельзя (`NaN` в замере портит средние, строка без часов невидима
 *  обоим индексам), а выбрасывать — значит потерять её навсегда, потому что
 *  курсор докачки за неё уже сдвинулся и второй раз её никто не предложит.
 *
 *  Ключ — тройка «таблица, профиль, серверный номер». Его достаточно, чтобы
 *  сохранить строку даже когда в самом содержимом нет годного `id`: колонки
 *  `profile_id` и `server_seq` приходят от сервера и проверены политикой.
 *
 *  Свойство, которое это даёт: каждая серверная строка либо применена, либо
 *  лежит здесь. Потеря становится отложенной, а не безвозвратной. */
/** Строка, которую сервер отверг навсегда и которую поэтому пропустили.
 *
 *  Хранится ССЫЛКОЙ, а не копией: сама строка никуда не делась, она лежит в
 *  журнале и видна ребёнку в истории. Здесь нужен только след — что именно
 *  не уехало и почему, — чтобы экран мог сказать об этом и на следующем
 *  обмене, и через неделю. */
export interface NotSentRow {
  table: SyncTable;
  id: string;
  profileId: string;
  reason: string;
  atMs: number;
}

export interface QuarantineRow {
  table: SyncTable;
  profileId: string;
  serverSeq: number;
  payload: unknown;
  reason: string;
  atMs: number;
}

/** Индекс чтения журнала. Тот же порядок, что даёт `compareSeq`, и он
 *  обязан начинаться с `profileId`: без этого диапазонного чтения внутри
 *  профиля нет вовсе. */
export const READ_INDEX = '[profileId+seq.ms+seq.counter+id]';

/** Индекс отправки. `deviceId` в префиксе — потому что курсор отправки
 *  обязан ходить по пространству, куда пишет только это устройство:
 *  докачка кладёт чужие строки в ту же таблицу, а часы другого устройства
 *  могут уйти вперёд, и наивная форма `seq > pushedUpTo` навсегда оставила
 *  бы собственные последующие записи ниже курсора (раздел 4.1).
 *
 *  Отличие от плана: в хвосте добавлен `id`. План заканчивал индекс на
 *  `counter`, но пара `(ms, counter)` не уникальна даже в пределах одного
 *  устройства — две вкладки, прочитавшие часы из состояния одновременно,
 *  дают одинаковую отметку. Курсор отправки — это позиция, возобновление
 *  идёт «строго выше» неё, и на одинаковом ключе вторая строка была бы
 *  пропущена навсегда. Молча, как и всё остальное в этом списке. Значит и
 *  `pushedUpTo` хранит тройку, а не пару. */
export const PUSH_INDEX = '[deviceId+profileId+seq.ms+seq.counter+id]';

const V1_STORES = {
  attempts: 'id, sessionId, *factIds, [seq.ms+seq.counter+seq.deviceId]',
  reveals: 'id, factId, revealedAt',
  sessions: 'id, startedAt',
  state: 'id',
};

const V2_STORES = {
  profiles: `id, createdAt, &[color+animal]`,
  device: 'id',
  attempts: `id, sessionId, *factIds, ${READ_INDEX}, ${PUSH_INDEX}`,
  reveals: `id, factId, revealedAt, ${READ_INDEX}, ${PUSH_INDEX}`,
  sessions: `id, startedAt, [profileId+startedAt], ${READ_INDEX}, ${PUSH_INDEX}`,
  state: 'id',
  syncState: '[profileId+table]',
};

/** Единственное отличие версии 3 от второй — уникальность пары «цвет и
 *  зверёк» снята с индекса.
 *
 *  Не послабление, а следствие надгробий. Удалённый профиль остаётся в
 *  таблице строкой с `deletedAt`, и уникальный индекс держал бы его пару
 *  занятой навсегда: родитель, удаливший синего ежа, не завёл бы синего ежа
 *  больше никогда, хотя место свободно. Сервер решил ровно это частичным
 *  индексом «уникально среди живых» (`20260901120600_soft_delete.sql`), а
 *  IndexedDB частичных индексов не знает вовсе.
 *
 *  Значит уникальность переезжает в транзакцию репозитория, где условие
 *  «среди живых» выразимо. Проверка при этом не ослабла: индекс отвергал
 *  только вставку, а транзакция отвергает и смену облика на занятый. */
const V3_STORES = {
  profiles: `id, createdAt, [color+animal]`,
};

const V4_STORES = {
  quarantine: '[table+profileId+serverSeq], profileId',
};

/** Шестая версия: строки, которые сервер не примет НИКОГДА.
 *
 *  Зеркало карантина, только для другой стороны обмена. У докачки
 *  испорченная СЕРВЕРНАЯ строка уезжает в карантин, курсор через неё
 *  перешагивает, и `countQuarantined` считает её при каждом пересчёте — то
 *  есть след постоянный.
 *
 *  У отправки следа не было. Пропущенная строка оставалась в журнале, но ни
 *  один счётчик её больше не видел: курсор ушёл вперёд, а число
 *  непринятых жило в ответе одного обмена. Родитель, не посмотревший на
 *  экран в тот раз, не узнавал никогда — а занятия так и не появлялись на
 *  втором устройстве.
 *
 *  Ключ здесь `[table+id]`, а не серверный номер: у местной строки его нет
 *  и быть не может, она до сервера не доехала. */
const V6_STORES = {
  ...V4_STORES,
  notSent: '[table+id], profileId',
};

/** Переход с версии 1 на версию 2 — **снос учебных данных, а не перенос**.
 *
 *  Решение владельца (2026-09-01): на выложенное приложение заходили только
 *  тестировщики, их журналы ценности не имеют, а тащить их через смену
 *  модели — значит тащить и мусор, записанный до появления сторожа: замеры
 *  `NaN`, отметки вне границ, строки без провенанса. Новый вход такие строки
 *  уже не принимает, и оставлять их в базе означало бы держать данные, по
 *  которым считается освоенность, ниже той планки, которую мы только что
 *  установили.
 *
 *  Что переживает переход: строка устройства. Идентификатор устройства и
 *  признак постоянного хранилища — не про ребёнка, и их потеря только
 *  заставила бы заново просить постоянное хранилище.
 *
 *  Профиль здесь не заводится: его создаёт `ensureActiveProfile` при первом
 *  запуске, и второй заводитель рядом означал бы два места, которые обязаны
 *  договариваться о цвете и звере.
 *
 *  Не экспортируется намеренно. Единственный вызывающий — обработчик
 *  обновления Dexie, который срабатывает ровно один раз; вызов этой функции
 *  на живой базе версии 2 стёр бы настоящий журнал ребёнка. */
async function migrateToV2(tx: Transaction): Promise<void> {
  const state = tx.table<Record<string, unknown>, string>('state');
  const singleton = await state.get('singleton');

  await Promise.all([
    tx.table('attempts').clear(),
    tx.table('reveals').clear(),
    tx.table('sessions').clear(),
    state.clear(),
  ]);

  await tx.table<DeviceRow, string>('device').put({
    id: 'singleton',
    deviceId: (singleton?.deviceId as string | undefined) ?? crypto.randomUUID(),
    storagePersisted: (singleton?.storagePersisted as boolean | null | undefined) ?? null,
    activeProfileId: null,
    schemaVersion: SCHEMA_VERSION,
    ownerId: null,
  });
}

/** Переход с версии 2 на версию 3 — **перенос, а не снос**.
 *
 *  Отличие от предыдущего перехода принципиальное, и его стоит проговорить:
 *  там сносились учебные данные тестировщиков по решению владельца, здесь
 *  сносить нечего и незачем. Меняется одна вещь — у профилей появляется
 *  поле надгробия, и существующим строкам его надо проставить.
 *
 *  Без явного `deletedAt: null` поле осталось бы `undefined`, а `undefined`
 *  и `null` в проверке `deletedAt === null` — разные вещи: живой профиль,
 *  заведённый до этого перехода, перестал бы считаться живым и исчез бы с
 *  экрана выбора вместе со своими занятиями. Данные при этом целы, что
 *  делает поломку только тише.
 *
 *  Не экспортируется по той же причине, что и переход на вторую версию:
 *  единственный законный вызывающий — обработчик обновления Dexie. */
async function migrateToV3(tx: Transaction): Promise<void> {
  await tx
    .table<Record<string, unknown>, string>('profiles')
    .toCollection()
    .modify((row) => {
      row.deletedAt = null;
      /** Существующие профили заведены до появления сервера — значит их
       *  надо отправить, а не считать уже известными. */
      row.needsPush = true;
      row.schemaVersion = SCHEMA_VERSION;
    });
}

/** Переход с версии 4 на версию 5: у строки устройства появляется владелец.
 *
 *  Проставить его явным `null` обязательно, и причина та же, что была у
 *  надгробий: `undefined` и `null` — разные вещи, а решение о том, стирать
 *  ли чужие данные, читает именно это поле. Строка, у которой поля нет
 *  вовсе, провалилась бы в ветку «чужой владелец» и предложила бы взрослому
 *  стереть занятия его собственных детей.
 *
 *  Почему `null`, а не «нынешний вошедший». Кто вошёл, на момент обновления
 *  базы неизвестно: обновление Dexie случается при открытии базы, а сессия
 *  разбирается позже. `null` означает «усыновит тот, кто войдёт первым», и
 *  для установки, где сессия просто продолжилась, это тот же родитель.
 *  Остаётся узкое окно — обновление, отсутствие сессии и вход ДРУГОГО
 *  родителя, — и в этом окне поведение ровно такое, каким было всегда. */
async function migrateToV5(tx: Transaction): Promise<void> {
  await tx
    .table<Record<string, unknown>, string>('device')
    .toCollection()
    .modify((row) => {
      row.ownerId = null;
      row.schemaVersion = SCHEMA_VERSION;
    });
}

/** Центральный слой Dexie: КАЖДЫЙ запрос к КАЖДОЙ таблице проходит через
 *  него (DBCore middleware, `db.use({ stack: 'dbcore', ... })`) —
 *  независимо от того, обёрнут ли вызывающий `сПовторомПриОбрыве`
 *  (`dbReconnect.ts`).
 *
 *  ЗАЧЕМ отдельно от неё. Из ~23 точек входа `src/data/repository.ts`
 *  обёрнуты ею только три; `отпустить(loadDevice())` и прочие при обрыве
 *  соединения только сообщают об отказе, а базу не переоткрывают — и
 *  СЛЕДУЮЩИЙ запрос падает тем же самым отказом снова, до перезагрузки
 *  страницы. Этот слой не лечит и не повторяет ТЕКУЩИЙ запрос — повтор
 *  внутри уже открытой (и, возможно, наполовину исполненной сверх этого
 *  запроса) транзакции невозможен, — он только закрывает и заново открывает
 *  соединение, чтобы СЛЕДУЮЩИЙ запрос, чей бы вызывающий ни был, пошёл по
 *  здоровому. Отказ ТЕКУЩЕГО запроса пробрасывается/возвращается дальше как
 *  есть в обоих случаях ниже.
 *
 *  Поколение снимается ДО вызова нижнего слоя (аргумент вычисляется раньше
 *  тела стрелочной функции), а не в `catch`: к моменту отказа кто-то другой
 *  — параллельный запрос через тот же слой, `сПовторомПриОбрыве` на том же
 *  самом обрыве — мог уже переоткрыть это же соединение, и
 *  `попытатьсяПродвинутьПоколение` не даст закрыть его второй раз (тот же
 *  довод, что у `dbReconnect.ts`, `М1`).
 *
 *  `mutate` — особый случай, и вот почему его мало обернуть в `catch`. У
 *  пакетных операций (`bulkPut`/`bulkAdd`/…) Dexie на асинхронном отказе
 *  ОДНОГО запроса не отклоняет промис САМОЙ `mutate()` — гасит отказ
 *  (`preventDefault`, `dexie.mjs`, функция `mutate`, ~3349–3430) и
 *  резолвится УСПЕХОМ с `{ numFailures, failures }`; `BulkError` бросает
 *  только слой ВЫШЕ (`Table.bulkPut()` и соседи, ~1806), которого этот
 *  middleware не видит вовсе — он сидит НИЖЕ него, у самого DBCore. Значит
 *  обрыв внутри пачки нужно искать в `failures` УСПЕШНОГО ответа, а не
 *  только в отклонённом промисе.
 *
 *  **Закрывать — только СВОЁ, ТЕКУЩЕЕ соединение (правка ревью 2026-09-26,
 *  дефект А).** Поколение снимается ДО запроса, а запрос может провисеть
 *  сколько угодно (медленная транзакция, вкладка в фоне): к моменту его
 *  отказа кто-то ДРУГОЙ мог уже поймать СВОЙ обрыв и переоткрыть базу —
 *  тогда отказавший запрос идёт по СТАРОМУ, уже заменённому соединению, а
 *  `dexieDb.close()` без этой проверки закрыл бы НОВОЕ, здоровое,
 *  оборвав транзакции, вставшие в очередь уже на нём. Поколение одно и то
 *  же не спасает: оба переоткрытия могли начаться раньше, чем каждое из
 *  них успело продвинуть счётчик (та же гонка, что и у М1, только по
 *  времени ответа запроса, а не по порядку каких), поэтому здесь сверяем
 *  не счётчик, а сам физический объект соединения — `req.trans` у
 *  бэкенда IndexedDB это НАСТОЯЩИЙ `IDBTransaction` (не абстракция ядра
 *  Dexie: `dbcore-indexeddb` зовёт `trans.objectStore(...)` напрямую), и
 *  `.db` у него — то самое `IDBDatabase`, что `dexieDb.backendDB()` отдаёт
 *  для ТЕКУЩЕГО соединения. Не совпало — запрос уже устарел сам по себе,
 *  закрывать нечего, и поколение остаётся ВТОРОЙ мерой (защищает от двух
 *  закрытий ОДНОГО и того же, ещё актуального, соединения). */
function installConnectionGuard(dexieDb: Dexie): void {
  dexieDb.use({
    stack: 'dbcore',
    name: 'connectionGuard',
    create(down: DBCore): Partial<DBCore> {
      return {
        table(tableName: string): DBCoreTable {
          const downTable: DBCoreTable = down.table(tableName);

          const своёСоединение = (trans: DBCoreTransaction): boolean =>
            (trans as unknown as IDBTransaction).db === dexieDb.backendDB();

          const переоткрытьЕслиОбрыв = (снятое: number, trans: DBCoreTransaction, ошибка: unknown): void => {
            if (!своёСоединение(trans)) return;
            if (попытатьсяПродвинутьПоколение(снятое)) {
              следПереоткрытия(имяОтказа(ошибка));
              dexieDb.close({ disableAutoOpen: false });
              отпустить(довестиОткрытие(dexieDb), 'переоткрытие');
            }
          };

          const сторож = <T,>(снятое: number, trans: DBCoreTransaction, result: Promise<T>): Promise<T> =>
            result.catch((error: unknown) => {
              if (отказСоединения(error)) переоткрытьЕслиОбрыв(снятое, trans, error);
              throw error;
            });

          return {
            ...downTable,
            get: (req) => сторож(текущееПоколение(), req.trans, downTable.get(req)),
            getMany: (req) => сторож(текущееПоколение(), req.trans, downTable.getMany(req)),
            query: (req) => сторож(текущееПоколение(), req.trans, downTable.query(req)),
            /** Открытие курсора мало обернуть: WebKit роняет обрыв и на
             *  самом `openCursor`, и на ПРОДВИЖЕНИИ уже открытого курсора
             *  (дефект D) — а Dexie гонит итерацию курсора не через
             *  `continue()` напрямую (он синхронный и без результата), а
             *  через промис `cursor.start(onNext)`, которым и обёрнута вся
             *  дорога опроса. Патчим `start` НА ТОМ ЖЕ объекте курсора (не
             *  копией через `{...cursor}` — `key`/`value`/`done` там
             *  геттеры, и копия заморозила бы их на первом же значении, а
             *  `continue`/`advance` потеряли бы верный `this`). */
            openCursor: (req) => {
              const снятое = текущееПоколение();
              return сторож(снятое, req.trans, downTable.openCursor(req)).then((cursor) => {
                if (!cursor) return cursor;
                const исходныйStart = cursor.start.bind(cursor);
                cursor.start = (onNext: () => void) => сторож(снятое, req.trans, исходныйStart(onNext));
                return cursor;
              });
            },
            count: (req) => сторож(текущееПоколение(), req.trans, downTable.count(req)),
            mutate: (req) => {
              const снятое = текущееПоколение();
              return downTable.mutate(req).then(
                (response) => {
                  const виновник = Object.values(response.failures ?? {}).find(
                    (failure) => отказСоединения(failure),
                  );
                  if (виновник !== undefined) переоткрытьЕслиОбрыв(снятое, req.trans, виновник);
                  return response;
                },
                (error: unknown) => {
                  if (отказСоединения(error)) переоткрытьЕслиОбрыв(снятое, req.trans, error);
                  throw error;
                },
              );
            },
          };
        },
      };
    },
  });
}

export class TrainerDb extends Dexie {
  profiles!: Table<ProfileRow, string>;
  device!: Table<DeviceRow, string>;
  attempts!: Table<AttemptRow, string>;
  reveals!: Table<RevealRow, string>;
  sessions!: Table<SessionRow, string>;
  state!: Table<StateRow, string>;
  syncState!: Table<SyncStateRow, [string, SyncTable]>;
  quarantine!: Table<QuarantineRow, [SyncTable, string, number]>;
  notSent!: Table<NotSentRow, [SyncTable, string]>;

  /** Имя базы — довод, а не константа: миграция проверяется только на
   *  отдельной базе, которую тест заводит по версии 1 и открывает по
   *  версии 2. */
  constructor(name = 'mult-trainer') {
    super(name);
    /** Составной индекс с участием factIds невозможен — это ограничение
     *  IndexedDB, не Dexie (раздел 4.3). `seq` — объект `Hlc`, а объект не
     *  может быть ключом IndexedDB; составной индекс по его полям даёт
     *  ровно тот же порядок, что `compareSeq`, потому что массивные ключи
     *  сравниваются поэлементно. */
    this.version(1).stores(V1_STORES);
    this.version(2)
      .stores(V2_STORES)
      .upgrade((tx) => migrateToV2(tx));
    this.version(3)
      .stores(V3_STORES)
      .upgrade((tx) => migrateToV3(tx));
    /** Четвёртая версия добавляет одну таблицу и ничего не переносит:
     *  обработчик обновления ей не нужен. */
    this.version(4).stores(V4_STORES);
    this.version(5).stores(V4_STORES).upgrade((tx) => migrateToV5(tx));
    /** Шестая версия добавляет одну таблицу и ничего не переносит:
     *  обработчик обновления ей не нужен. */
    this.version(6).stores(V6_STORES);

    /** До первого открытия — `use()` только регистрирует middleware в
     *  `this._middlewares`, а стек DBCore собирается заново при КАЖДОМ
     *  `dexieOpen` (в т.ч. при переоткрытии после `close()`), так что
     *  регистрация здесь, в конструкторе, действует и на самое первое
     *  открытие, и на каждое переоткрытие после обрыва. */
    installConnectionGuard(this);
  }
}

export const db = new TrainerDb();
